import React, { useCallback, useEffect, useMemo, useState } from 'react';
import flyerService, {
  FLYER_FORMATS,
  Flyer,
  FlyerFormat,
  ReadyPostIdea,
} from '../services/flyerService';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { rollenAufzaehlung, rollenMitRecht } from '../verwaltung/rollen';

const FORMAT_LABELS: Record<FlyerFormat, string> = {
  pdf: 'PDF (A4, druckbar)',
  linkedin: 'LinkedIn 1200×627',
  square: 'Instagram Square 1080×1080',
  story: 'Instagram Story 1080×1920',
};

const FlyerGeneratorPage: React.FC = () => {
  // Veroeffentlichen (POST /flyers/:id/post-linkedin) verlangt
  // linkedin.freigeben (Welle 2); Erzeugen bleibt bei inhalte.bearbeiten.
  const { darf } = useBerechtigung();
  const darfFreigeben = darf('linkedin.freigeben');
  const [ideas, setIdeas] = useState<ReadyPostIdea[]>([]);
  const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<FlyerFormat[]>([...FLYER_FORMATS]);
  const [loadingIdeas, setLoadingIdeas] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [posting, setPosting] = useState(false);
  const [caption, setCaption] = useState('');
  const [currentFlyer, setCurrentFlyer] = useState<Flyer | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Partial<Record<FlyerFormat, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const selectedIdea = useMemo(
    () => ideas.find((idea) => idea.id === selectedIdeaId) ?? null,
    [ideas, selectedIdeaId]
  );

  const loadIdeas = useCallback(async () => {
    try {
      setLoadingIdeas(true);
      const data = await flyerService.listReadyPostIdeas();
      setIdeas(data);
      if (data.length > 0 && !selectedIdeaId) {
        setSelectedIdeaId(data[0].id);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Fehler beim Laden');
    } finally {
      setLoadingIdeas(false);
    }
  }, [selectedIdeaId]);

  useEffect(() => {
    loadIdeas();
  }, [loadIdeas]);

  useEffect(() => {
    if (selectedIdea) {
      const defaultCaption =
        selectedIdea.contentBrief?.fullBrief ??
        selectedIdea.description ??
        selectedIdea.title;
      setCaption(defaultCaption ?? '');
    } else {
      setCaption('');
    }
    setCurrentFlyer(null);
    setPreviewUrls({});
  }, [selectedIdea]);

  const toggleFormat = (format: FlyerFormat) => {
    setSelectedFormats((prev) =>
      prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]
    );
  };

  const clearPreviews = () => {
    setPreviewUrls((prev) => {
      Object.values(prev).forEach((url) => url && URL.revokeObjectURL(url));
      return {};
    });
  };

  const loadPreviews = async (flyer: Flyer) => {
    clearPreviews();
    const next: Partial<Record<FlyerFormat, string>> = {};
    for (const format of FLYER_FORMATS) {
      const hasFile = (() => {
        switch (format) {
          case 'pdf':
            return !!flyer.pdfPath;
          case 'linkedin':
            return !!flyer.linkedinImagePath;
          case 'square':
            return !!flyer.instaSquarePath;
          case 'story':
            return !!flyer.instaStoryPath;
          default:
            return false;
        }
      })();
      if (!hasFile) continue;
      try {
        const blob = await flyerService.fetchBlob(flyer.id, format);
        next[format] = URL.createObjectURL(blob);
      } catch {
        /* ignore individual format failures */
      }
    }
    setPreviewUrls(next);
  };

  const handleGenerate = async () => {
    if (!selectedIdeaId) {
      setError('Bitte zuerst eine Beitragsidee auswählen.');
      return;
    }
    if (selectedFormats.length === 0) {
      setError('Bitte mindestens ein Format wählen.');
      return;
    }
    setError(null);
    setInfo(null);
    try {
      setGenerating(true);
      const flyer = await flyerService.generate({
        postIdeaId: selectedIdeaId,
        formats: selectedFormats,
      });
      setCurrentFlyer(flyer);
      setInfo('Flyer wurde erzeugt.');
      await loadPreviews(flyer);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Fehler bei der Erzeugung');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (format: FlyerFormat) => {
    if (!currentFlyer) return;
    try {
      await flyerService.triggerDownload(currentFlyer.id, format);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Download fehlgeschlagen');
    }
  };

  const handlePostLinkedIn = async () => {
    if (!currentFlyer) return;
    setError(null);
    setInfo(null);
    try {
      setPosting(true);
      const updated = await flyerService.postToLinkedIn(currentFlyer.id, caption);
      setCurrentFlyer(updated);
      setInfo(
        updated.linkedinPostUrl
          ? `Auf LinkedIn veröffentlicht: ${updated.linkedinPostUrl}`
          : 'Auf LinkedIn veröffentlicht.'
      );
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'LinkedIn-Post fehlgeschlagen');
    } finally {
      setPosting(false);
    }
  };

  useEffect(() => () => clearPreviews(), []);

  return (
    <div className="page-container" style={{ padding: 24 }}>
      <PageHeader title="Flyer Generator" subtitle={routeById('li-flyer-new')?.description} />

      {error && <div className="alert alert-error mb-4">{error}</div>}
      {info && <div className="alert alert-success mb-4">{info}</div>}

      <div className="flex gap-4" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: '1 1 320px', minWidth: 280, padding: 16 }}>
          <h3>Ready Beitragsideen</h3>
          {loadingIdeas ? (
            <p className="text-muted">Wird geladen…</p>
          ) : ideas.length === 0 ? (
            <p className="text-muted">
              Keine Ideen gefunden. Ideen sind ready, sobald sie Status „created" / „scheduled"
              haben oder ein Content Brief verknüpft ist.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {ideas.map((idea) => (
                <li
                  key={idea.id}
                  onClick={() => setSelectedIdeaId(idea.id)}
                  style={{
                    padding: '10px 12px',
                    marginBottom: 6,
                    borderRadius: 6,
                    border:
                      idea.id === selectedIdeaId
                        ? '2px solid var(--sec-info)'
                        : '1px solid var(--sec-border)',
                    cursor: 'pointer',
                    background: idea.id === selectedIdeaId ? 'color-mix(in srgb, var(--sec-info) 10%, var(--sec-surface))' : '#fff',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{idea.title}</div>
                  <div className="text-sm text-muted">
                    {idea.seriesFormat?.name ?? 'Keine Serie'} · {idea.status}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card" style={{ flex: '2 1 480px', minWidth: 360, padding: 16 }}>
          <h3>Formate auswählen</h3>
          {FLYER_FORMATS.map((format) => (
            <label key={format} className="flex gap-2" style={{ alignItems: 'center', marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={selectedFormats.includes(format)}
                onChange={() => toggleFormat(format)}
              />
              <span>{FORMAT_LABELS[format]}</span>
            </label>
          ))}

          {darfFreigeben && (
          <div style={{ marginTop: 16 }}>
            <label className="form-label" htmlFor="flyer-caption">
              LinkedIn-Caption (für „Auf LinkedIn veröffentlichen")
            </label>
            <textarea
              id="flyer-caption"
              className="form-input"
              rows={4}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          )}

          <div className="flex gap-2" style={{ marginTop: 16 }}>
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={generating || !selectedIdeaId || selectedFormats.length === 0}
            >
              {generating ? 'Erzeuge…' : 'Flyer erzeugen'}
            </button>
            {darfFreigeben && (
            <button
              className="btn btn-secondary"
              onClick={handlePostLinkedIn}
              disabled={
                posting ||
                !currentFlyer ||
                !currentFlyer.linkedinImagePath ||
                currentFlyer.status === 'posted'
              }
            >
              {posting ? 'Poste…' : 'Auf LinkedIn veröffentlichen'}
            </button>
            )}
          </div>
          {!darfFreigeben && (
            <p className="text-sm text-muted" data-testid="rechte-hinweis" style={{ marginTop: 8 }}>
              Auf LinkedIn veröffentlichen ist {rollenAufzaehlung(rollenMitRecht('linkedin.freigeben'))} vorbehalten.
            </p>
          )}

          {currentFlyer && (
            <div style={{ marginTop: 24 }}>
              <h4>Vorschau &amp; Downloads</h4>
              <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                {FLYER_FORMATS.map((format) => {
                  const url = previewUrls[format];
                  return (
                    <div
                      key={format}
                      style={{
                        border: '1px solid var(--sec-border)',
                        borderRadius: 6,
                        padding: 8,
                        width: 200,
                      }}
                    >
                      <div className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>
                        {FORMAT_LABELS[format]}
                      </div>
                      {format !== 'pdf' && url ? (
                        <img
                          src={url}
                          alt={format}
                          style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                        />
                      ) : (
                        <div className="text-sm text-muted">
                          {url ? 'Bereit' : 'Nicht generiert'}
                        </div>
                      )}
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={!url}
                        onClick={() => handleDownload(format)}
                        style={{ marginTop: 6, width: '100%' }}
                      >
                        Download
                      </button>
                    </div>
                  );
                })}
              </div>
              {currentFlyer.linkedinPostUrl && (
                <p className="text-sm" style={{ marginTop: 12 }}>
                  Gepostet:{' '}
                  <a href={currentFlyer.linkedinPostUrl} target="_blank" rel="noreferrer">
                    {currentFlyer.linkedinPostUrl}
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlyerGeneratorPage;
